import { BiDotsVerticalRounded } from 'react-icons/bi'
import { Tooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useThemeMode } from '../../_metronic/partials/layout/theme-mode/ThemeModeProvider'

type TableSettingMenuProps = {
    children?: React.ReactNode
    tooltipContent?: string | false
    /** Unique tooltip id so multiple menus on one page show distinct titles. */
    tooltipId?: string
    /** Panel header title (defaults to Display Menu). */
    menuTitle?: string
    /** Preferred open direction; auto flips when there is not enough space. */
    placement?: 'up' | 'down' | 'auto'
}

type MenuCoords = {
    top: number
    left: number
    maxHeight: number
}

const MENU_GAP = 6
const MENU_WIDTH = 325
const VIEWPORT_PAD = 8
const ESTIMATED_MENU_HEIGHT = 360

const TableSettingMenu = ({
    children,
    tooltipContent = 'Columns Menu',
    tooltipId,
    menuTitle = 'Display Menu',
    placement = 'auto',
}: TableSettingMenuProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [coords, setCoords] = useState<MenuCoords | null>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    const instanceId = useId().replace(/:/g, '')
    const resolvedTooltipId = tooltipId || `setting-menu-${instanceId}`
    const { mode: themeMode } = useThemeMode()
    const htmlTheme =
        typeof document !== 'undefined'
            ? document.documentElement.getAttribute('data-bs-theme')
            : null
    const isDark =
        themeMode === 'dark' ||
        htmlTheme === 'dark' ||
        (themeMode === 'system' &&
            typeof window !== 'undefined' &&
            window.matchMedia('(prefers-color-scheme: dark)').matches)

    const computeCoords = () => {
        const trigger = triggerRef.current
        if (!trigger) return

        const rect = trigger.getBoundingClientRect()
        const vw = window.innerWidth
        const vh = window.innerHeight
        const spaceBelow = vh - rect.bottom - VIEWPORT_PAD
        const spaceAbove = rect.top - VIEWPORT_PAD
        const measured = panelRef.current?.offsetHeight || ESTIMATED_MENU_HEIGHT

        let openUp = placement === 'up'
        if (placement === 'auto') {
            openUp = spaceBelow < Math.min(measured, ESTIMATED_MENU_HEIGHT) && spaceAbove > spaceBelow
        } else if (placement === 'down' && spaceBelow < 180 && spaceAbove > spaceBelow) {
            openUp = true
        } else if (placement === 'up' && spaceAbove < 180 && spaceBelow > spaceAbove) {
            openUp = false
        }

        const maxHeight = Math.max(160, Math.min(400, openUp ? spaceAbove - MENU_GAP : spaceBelow - MENU_GAP))
        const panelHeight = Math.min(measured, maxHeight)

        let top = openUp ? rect.top - MENU_GAP - panelHeight : rect.bottom + MENU_GAP
        top = Math.max(VIEWPORT_PAD, Math.min(top, vh - VIEWPORT_PAD - Math.min(panelHeight, maxHeight)))

        let left = rect.right - MENU_WIDTH
        left = Math.max(VIEWPORT_PAD, Math.min(left, vw - MENU_WIDTH - VIEWPORT_PAD))

        setCoords({ top, left, maxHeight })
    }

    useLayoutEffect(() => {
        if (!isOpen) {
            setCoords(null)
            return
        }
        computeCoords()
        const raf = requestAnimationFrame(() => computeCoords())
        return () => cancelAnimationFrame(raf)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, placement])

    useEffect(() => {
        if (!isOpen) return

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node
            if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) {
                return
            }
            setIsOpen(false)
        }
        const handleReposition = () => computeCoords()

        document.addEventListener('mousedown', handleClickOutside)
        window.addEventListener('resize', handleReposition)
        window.addEventListener('scroll', handleReposition, true)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            window.removeEventListener('resize', handleReposition)
            window.removeEventListener('scroll', handleReposition, true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, placement])

    const panel =
        isOpen && typeof document !== 'undefined'
            ? createPortal(
                <div
                    ref={panelRef}
                    className={`table-setting-menu w-300px w-md-325px text-start${isDark ? ' table-setting-menu--dark' : ''}`}
                    data-bs-theme={isDark ? 'dark' : 'light'}
                    style={{
                        position: 'fixed',
                        top: coords?.top ?? 0,
                        left: coords?.left ?? 0,
                        width: MENU_WIDTH,
                        zIndex: 99999,
                        maxHeight: coords?.maxHeight ?? 400,
                        overflow: 'auto',
                        display: 'block',
                        visibility: coords ? 'visible' : 'hidden',
                        backgroundColor: isDark ? '#1e1e2d' : '#ffffff',
                        color: isDark ? '#e4e6ef' : undefined,
                        boxShadow: isDark
                            ? '0 0 50px 0 rgba(0, 0, 0, 0.45)'
                            : '0 0 50px 0 rgba(82, 63, 105, 0.15)',
                        border: isDark ? '1px solid #323248' : '1px solid #eff2f5',
                        borderRadius: '0.475rem',
                    }}
                >
                    <div className="px-7 py-5">
                        <div
                            className="fs-5 fw-bold table-setting-menu-title"
                            style={{ color: isDark ? '#e4e6ef' : '#3f4254' }}
                        >
                            {menuTitle}
                        </div>
                    </div>

                    <div className={`separator ${isDark ? 'border-gray-700' : 'border-gray-200'}`}></div>

                    <div className="px-7 py-5" data-kt-user-table-filter="form">
                        <div
                            className="overflow-auto sm-scroll table-setting-menu-body"
                            style={{
                                maxHeight: Math.max(120, (coords?.maxHeight ?? 400) - 110),
                                ...(isDark ? { color: '#e4e6ef' } : {}),
                            }}
                        >
                            {children}
                        </div>
                    </div>
                </div>,
                document.body
            )
            : null

    return (
        <>
            {tooltipContent && !isOpen ? (
                <Tooltip id={resolvedTooltipId} place="left" positionStrategy="fixed" />
            ) : null}
            <div style={{ position: 'relative', display: 'inline-block' }}>
                <button
                    ref={triggerRef}
                    type="button"
                    className="btn btn-sm pe-0 py-0"
                    onClick={(event) => {
                        event.stopPropagation()
                        setIsOpen((prev) => !prev)
                    }}
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    aria-label={typeof tooltipContent === 'string' ? tooltipContent : menuTitle}
                    title={typeof tooltipContent === 'string' ? tooltipContent : menuTitle}
                    data-tooltip-id={!isOpen && tooltipContent ? resolvedTooltipId : undefined}
                    data-tooltip-content={!isOpen && tooltipContent ? tooltipContent : undefined}
                >
                    <BiDotsVerticalRounded />
                </button>
                {panel}
            </div>
        </>
    )
}

export default TableSettingMenu
